import type { ReactNode } from "react";
import SVG from "react-inlinesvg";
import { cn } from "src/lib/cn";
import { SudoButton } from "src/sudokit/components/SudoButton";
import type { EntityImageListItem } from "./lib/types";
import { orderEntityImages } from "./lib/orderEntityImages";
import { getEntityImageAltText } from "./lib/imageAltText";
import { useFadeInImage } from "./lib/useFadeInImage";
import { RemoveImageButton } from "./TileActionsMenu";

type ShapeGroup = "squareOverPair" | "pairOverSquare" | "tallPortrait";

// Each row-group is a full permutation of the three shapes across the three columns — col1
// starts on squareOverPair, col2 on tallPortrait, col3 on pairOverSquare — and the cycle
// advances by one shape per row-group, reproducing the repeating diagonal pattern from the
// design spec (row1 = square/tall/pair, row2 = pair/square/tall, row3 = tall/pair/square).
const CYCLE: ShapeGroup[] = ["squareOverPair", "pairOverSquare", "tallPortrait"];
const COLUMN_OFFSETS = [0, 2, 1];
const groupForCell = (column: number, rowGroup: number): ShapeGroup => CYCLE[(COLUMN_OFFSETS[column] + rowGroup) % 3];

const MIN_ROW_GROUPS = 2;
const GAP = "gap-[10px]";
const SQUARE_ASPECT = "1 / 1";

interface EntityImageMasonryGalleryProps {
  entityName: string;
  images: EntityImageListItem[];
  onSelectImage: (imageId: string) => void;
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onRemoveImage?: (imageId: string) => Promise<void>;
}

export default function EntityImageMasonryGallery({ entityName, images, onSelectImage, isLoading, hasNextPage, isFetchingNextPage, onLoadMore, onRemoveImage }: EntityImageMasonryGalleryProps) {
  // While loading, always show a full minimum skeleton shape. Otherwise round up to a full
  // row-group so every loaded image renders — flooring here would silently drop already-loaded
  // images whenever they don't happen to fill a complete row-group.
  const rowGroupCount = isLoading ? MIN_ROW_GROUPS : Math.max(MIN_ROW_GROUPS, Math.ceil(images.length / 7));

  // Cover-first ordering guarantees the cover always lands in the first, top-left cell.
  const orderedImages = orderEntityImages(images);

  // Flatten the fill queue once, consumed row-group by row-group, column by column — this keeps
  // the cover (always orderedImages[0]) anchored near the top-left rather than scattered by shape.
  const queue = [...orderedImages];
  const take = (n: number): (EntityImageListItem | null)[] => Array.from({ length: n }, () => queue.shift() ?? null);

  // A single flat list in row-major order (row-group outer, column inner) rather than three
  // independent per-column arrays — placed directly as grid children, this lets CSS Grid's
  // default row auto-sizing/stretch keep all 3 shapes in a row-group the same height (see
  // ShapeGroupBlock), instead of each column growing independently.
  const blocks: ReactNode[] = [];
  for (let g = 0; g < rowGroupCount; g++) {
    for (let c = 0; c < 3; c++) {
      const shape = groupForCell(c, g);
      const cellCount = shape === "tallPortrait" ? 1 : 3;
      blocks.push(
        <ShapeGroupBlock
          key={`${c}-${g}`}
          shape={shape}
          entityName={entityName}
          cells={take(cellCount)}
          onCellClick={onSelectImage}
          onRemoveImage={onRemoveImage}
          // Empty (not-yet-filled) cells pulse only while a fetch is actually in flight (initial
          // load, or a "View more" click) — hasNextPage alone just means more *could* be loaded,
          // not that anything is happening right now, so it shouldn't pulse while idle.
          showSkeleton={isLoading || isFetchingNextPage}
        />,
      );
    }
  }

  return (
    <div className="-mb-6 mt-6 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pb-6">
      <div className={cn("grid grid-cols-3", GAP)}>{blocks}</div>
      {hasNextPage && (
        <div className="flex justify-center">
          <SudoButton
            type="button"
            variant="secondary"
            size="small"
            state={isFetchingNextPage ? "loading" : "default"}
            disabled={isFetchingNextPage}
            onClick={onLoadMore}
          >
            View more portraits
          </SudoButton>
        </div>
      )}
    </div>
  );
}

function EmptyCell({ aspect, className, skeleton }: { aspect?: string; className: string; skeleton?: boolean }) {
  return (
    <div
      // Tailwind's stock `animate-pulse` name collides with a legacy `@keyframes pulse` in
      // App.scss (an unrelated purple glow effect) — `animate-skeleton-pulse` is the same plain
      // opacity fade, registered under a name that doesn't collide (see tailwind.config.js).
      className={cn("rounded-xl bg-app-divider", skeleton && "animate-skeleton-pulse", className)}
      style={aspect ? { aspectRatio: aspect } : undefined}
      aria-hidden="true"
    />
  );
}

function PopulatedCell({
  image,
  entityName,
  aspect,
  className,
  onClick,
  onRemove,
}: {
  image: EntityImageListItem;
  entityName: string;
  aspect?: string;
  className: string;
  onClick: () => void;
  onRemove?: () => Promise<void>;
}) {
  const { loaded, ref, onLoad } = useFadeInImage(image.url);
  const isCover = image.status === "COVER";
  const altText = getEntityImageAltText(entityName, image.prompt);
  return (
    <div
      className={cn("group relative overflow-hidden rounded-xl border border-solid border-[color-mix(in_srgb,var(--grey-0)_10%,transparent)]", className)}
      style={aspect ? { aspectRatio: aspect } : undefined}
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={`View ${altText}`}
        className="use-component-styles m-0 block h-full w-full cursor-pointer overflow-hidden border-0 bg-transparent p-0"
      >
        <img
          ref={ref}
          src={image.url}
          alt={altText}
          loading="lazy"
          decoding="async"
          onLoad={onLoad}
          className={cn(
            "block h-full w-full origin-center transform-gpu object-cover transition-all [transition-duration:600ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] will-change-transform group-hover:scale-[1.08]",
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
        <span
          aria-hidden="true"
          className={cn("pointer-events-none absolute inset-0 animate-skeleton-pulse bg-app-divider transition-opacity duration-300", loaded && "opacity-0")}
        />
        {isCover && (
          <span
            aria-hidden="true"
            className="absolute right-1 bottom-1 z-10 flex h-5 w-5 items-center justify-center rounded-md bg-black/50"
          >
            <SVG
              src="/assets/star.fill.svg"
              width={11}
              height={11}
              className="[&_path]:fill-white"
              aria-hidden="true"
            />
          </span>
        )}
      </button>
      {onRemove && (
        <RemoveImageButton
          onRemove={onRemove}
        />
      )}
    </div>
  );
}

function ShapeGroupBlock({
  shape,
  entityName,
  cells,
  onCellClick,
  onRemoveImage,
  showSkeleton,
}: {
  shape: ShapeGroup;
  entityName: string;
  cells: (EntityImageListItem | null)[];
  onCellClick: (id: string) => void;
  onRemoveImage?: (imageId: string) => Promise<void>;
  showSkeleton?: boolean;
}) {
  const cell = (image: EntityImageListItem | null, aspect: string | undefined, className: string) =>
    image ? (
      <PopulatedCell
        key={image.id}
        image={image}
        entityName={entityName}
        aspect={aspect}
        className={className}
        onClick={() => onCellClick(image.id)}
        onRemove={onRemoveImage ? () => onRemoveImage(image.id) : undefined}
      />
    ) : (
      <EmptyCell
        aspect={aspect}
        className={className}
        skeleton={showSkeleton}
      />
    );

  if (shape === "tallPortrait") {
    // No aspect ratio here on purpose: every row-group always pairs exactly one tallPortrait
    // block with one squareOverPair and one pairOverSquare block (see CYCLE/COLUMN_OFFSETS
    // above), and those two set the row-group's natural height via their own square cells. All
    // three blocks are siblings in the same CSS Grid row, so leaving this cell's height unset
    // lets the grid's default row stretch expand it to match that height exactly, instead of
    // imposing its own fixed ratio that would drift out of sync with the square-based columns.
    return <div className="h-full w-full">{cell(cells[0], undefined, "h-full w-full")}</div>;
  }

  const [a, b, c] = cells;
  // squareOverPair: a = top square, b/c = bottom half-squares.
  // pairOverSquare: a/b = top half-squares, c = bottom square (mirror of squareOverPair).
  const topRow =
    shape === "squareOverPair" ? (
      cell(a, SQUARE_ASPECT, "w-full")
    ) : (
      <div className={cn("flex", GAP)}>
        {cell(a, SQUARE_ASPECT, "flex-1")}
        {cell(b, SQUARE_ASPECT, "flex-1")}
      </div>
    );
  const bottomRow =
    shape === "squareOverPair" ? (
      <div className={cn("flex", GAP)}>
        {cell(b, SQUARE_ASPECT, "flex-1")}
        {cell(c, SQUARE_ASPECT, "flex-1")}
      </div>
    ) : (
      cell(c, SQUARE_ASPECT, "w-full")
    );

  return (
    <div className={cn("flex w-full flex-col", GAP)}>
      {topRow}
      {bottomRow}
    </div>
  );
}
